"use client";

import {KeyRound, Settings, User} from "lucide-react";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import type {ClientSessionData} from "@/app/auth/session-types";
import {LocalizationKeys} from "@/lib/all-localization-keys";
import {LoaderIcon} from "@/components/icons";
import {PasswordChangeWarningDialog} from "@/components/password-change-warning-dialog";
import {SessionsList} from "@/components/sessions-list";
import {SignOutAllWarningDialog} from "@/components/sign-out-all-warning-dialog";
import {toast} from "@/components/toast";
import {Button} from "@/components/ui/button";
import {Separator} from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {useFeatureFlags, useSignOutAllSessions} from "@/hooks/use-api-endpoint";
import {useAuth} from "@/app/auth/hooks";
import {getConfig} from "@/hooks/use-config";
import {logError} from '@/lib/logging';

type AccountSettingsDialogProps = {
  children: React.ReactNode;
  user: ClientSessionData["user"];
};

export function AccountSettingsDialog({
                                        children,
                                        user,
                                      }: AccountSettingsDialogProps) {
  const {t} = useTranslation();
  const {signOut} = useAuth();
  const [open, setOpen] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isSignOutLoading, setIsSignOutLoading] = useState(false);
  const [showPasswordWarning, setShowPasswordWarning] = useState(false);
  const [showSignOutWarning, setShowSignOutWarning] = useState(false);
  const {userRole, isLoading: rolesLoading} = useFeatureFlags();
  const {deleteAllSessions} = useSignOutAllSessions();

  const handlePasswordChangeClick = () => {
    setShowPasswordWarning(true);
  };

  const handlePasswordChangeConfirm = async () => {
    setShowPasswordWarning(false);
    setIsPasswordLoading(true);

    try {
      // Fetch config (uses module-level cache after first call)
      const config = await getConfig();

      // Send password reset email via Auth0
      const auth0Domain = config.auth0Domain;
      const clientId = config.auth0ClientId;

      const response = await fetch(
        `https://${auth0Domain}/dbconnections/change_password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            client_id: clientId,
            email: user.email,
            connection: "Username-Password-Authentication",
          }),
        }
      );

      if (response.ok) {
        toast({
          type: "success",
          description: t(LocalizationKeys.SUCCESS_PASSWORD_RESET_SENT, {email: user.email}),
        });
      } else {
        const errorText = await response.text();
        logError({
            message: "Password reset error",
            errorText
        });
        toast({
          type: "error",
          description: t(LocalizationKeys.ERROR_PASSWORD_RESET_FAILED),
        });
      }
    } catch (error) {
      logError(error, {
          message: "Password reset error"
      });
      toast({
        type: "error",
        description: t(LocalizationKeys.ERROR_UNEXPECTED),
      });
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handleSignOutAllClick = () => {
    setShowSignOutWarning(true);
  };

  const handleSignOutAllConfirm = async () => {
    setShowSignOutWarning(false);
    setIsSignOutLoading(true);

    try {
      // Invalidate all sessions
      const response = await deleteAllSessions();

      if (response.ok) {
        toast({
          type: "success",
          description: t(LocalizationKeys.STATUS_SIGNING_OUT_ALL),
        });

        // Force sign-out after a short delay
        setTimeout(() => {
          signOut()
        }, 1000);
      } else {
        toast({
          type: "error",
          description: t(LocalizationKeys.ERROR_SIGN_OUT_ALL_FAILED),
        });
        setIsSignOutLoading(false);
      }
    } catch (error) {
      logError(error, {
          message: "Sign out all error"
      });
      toast({
        type: "error",
        description: t(LocalizationKeys.ERROR_UNEXPECTED),
      });
      setIsSignOutLoading(false);
    }
  };

  return (
    <Sheet onOpenChange={setOpen} open={open}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5"/>
            {t(LocalizationKeys.ACCOUNT_SETTINGS)}
          </SheetTitle>
          <SheetDescription>
            {t(LocalizationKeys.ACCOUNT_SETTINGS_DESCRIPTION)}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-6 py-4">
          {/* User Information Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 font-medium text-sm">
              <User className="h-4 w-4 text-muted-foreground"/>
              <span>{t(LocalizationKeys.ACCOUNT_INFO_HEADER)}</span>
            </div>
            <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs">{t(LocalizationKeys.ACCOUNT_EMAIL_LABEL)}</p>
                <p className="font-medium text-sm">{user.email}</p>
              </div>
              {!rolesLoading && userRole && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs">{t(LocalizationKeys.ACCOUNT_ROLES_LABEL)}</p>
                  <div className="flex flex-wrap gap-1">
                      <span
                        className="inline-flex items-center rounded-full bg-brand-primary-light px-2.5 py-0.5 font-medium text-brand-primary-dark text-xs"
                      >
                        {userRole}
                      </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Separator/>

          {/* Password Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 font-medium text-sm">
              <KeyRound className="h-4 w-4 text-muted-foreground"/>
              <span>{t(LocalizationKeys.ACCOUNT_SECURITY_HEADER)}</span>
            </div>

            <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
              <div className="space-y-1">
                <p className="font-medium text-sm">{t(LocalizationKeys.ACCOUNT_PASSWORD_LABEL)}</p>
                <p className="text-muted-foreground text-xs">
                  {t(LocalizationKeys.ACCOUNT_PASSWORD_DESCRIPTION)}
                </p>
              </div>
              <Button
                className="w-full"
                disabled={isPasswordLoading}
                onClick={handlePasswordChangeClick}
                size="sm"
                variant="outline"
              >
                {isPasswordLoading ? (
                  <>
                    <LoaderIcon className="mr-2 h-4 w-4 animate-spin"/>
                    {t(LocalizationKeys.BUTTON_SENDING)}
                  </>
                ) : (
                  t(LocalizationKeys.BUTTON_SEND_PASSWORD_RESET)
                )}
              </Button>
            </div>

            <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
              <div className="space-y-1">
                <p className="font-medium text-sm">{t(LocalizationKeys.ACCOUNT_SESSIONS_HEADER)}</p>
                <p className="text-muted-foreground text-xs">
                  {t(LocalizationKeys.ACCOUNT_SESSIONS_DESCRIPTION)}
                </p>
              </div>
              <SessionsList userId={user.id}/>
              <Button
                className="w-full"
                disabled={isSignOutLoading}
                onClick={handleSignOutAllClick}
                size="sm"
                variant="outline"
              >
                {isSignOutLoading ? (
                  <>
                    <LoaderIcon className="mr-2 h-4 w-4 animate-spin"/>
                    {t(LocalizationKeys.BUTTON_SIGNING_OUT)}
                  </>
                ) : (
                  t(LocalizationKeys.BUTTON_SIGN_OUT_ALL)
                )}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
      <PasswordChangeWarningDialog
        onConfirm={handlePasswordChangeConfirm}
        onOpenChange={setShowPasswordWarning}
        open={showPasswordWarning}
        userEmail={user.email}
      />
      <SignOutAllWarningDialog
        onConfirm={handleSignOutAllConfirm}
        onOpenChange={setShowSignOutWarning}
        open={showSignOutWarning}
      />
    </Sheet>
  );
}
